import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Menu, ArrowUp, ExternalLink, Sparkles, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import RoleSwitch from './RoleSwitch';
import GlobalSearch from './GlobalSearch';
import { TRUST_URL } from './Sidebar';

const READINESS_BREAKDOWN = [
  { label: 'Control coverage continuity', value: 94, weight: '40%', color: '#0D9488' },
  { label: 'Changes with approved baseline', value: 84, weight: '35%', color: '#0EA5E9' },
  { label: 'Exposure windows documented', value: 79, weight: '25%', color: '#8B5CF6' },
];

export default function Header() {
  const { seed, setDemoOpen, setSidebarOpen } = useApp();
  const ps = seed?.platform_status;

  const methodology = "Calculated from: % of controls with continuous coverage (40%) + % of approved changes with baseline (35%) + % of exposure windows with documentation (25%). Based on 14 months of CloudTrail history. 98.7% event coverage. Last computed: 4 minutes ago.";

  return (
    <header className="sticky top-0 z-30 glass-dark text-white border-b border-slate-800/80">
      <div className="flex items-center gap-3 px-3 sm:px-4 lg:px-6 h-14">
        {/* Mobile hamburger */}
        <button
          onClick={() => setSidebarOpen(true)}
          data-testid="sidebar-open"
          className="md:hidden text-slate-300 hover:text-white p-1.5 -ml-1.5"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Wordmark */}
        <Link to="/" data-testid="header-wordmark" className="flex items-center gap-2 mr-2">
          <span className="font-bold tracking-tight text-white text-lg">APEX</span>
        </Link>

        {/* Product switcher - hidden on mobile */}
        <div className="hidden md:flex items-center bg-slate-800/80 rounded-full p-0.5 gap-0.5 border border-slate-700">
          <span className="px-3 py-1 text-[11px] font-semibold rounded-full bg-apex-lens text-white">Lens</span>
          <a href={TRUST_URL} target="_blank" rel="noopener noreferrer" data-testid="header-trust-link" className="px-3 py-1 text-[11px] font-semibold rounded-full text-slate-400 hover:text-white inline-flex items-center gap-1">
            Trust
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>

        {/* Evidence Readiness */}
        {ps && (
          <>
            <div className="hidden sm:block">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    data-testid="evidence-readiness-trigger"
                    className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-800/60 hover:bg-slate-800 transition-colors cursor-pointer"
                  >
                    <span className="relative inline-block w-2 h-2 rounded-full bg-apex-lens">
                      <span className="absolute inset-0 rounded-full bg-apex-lens animate-ping opacity-60" />
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">Evidence Readiness</span>
                    <span className="text-sm font-bold text-white tabular">{ps.evidence_readiness}%</span>
                    <span className="text-[10px] text-emerald-400 inline-flex items-center"><ArrowUp className="w-2.5 h-2.5" />{ps.evidence_readiness_trend}%</span>
                    <TrendingUp className="w-3.5 h-3.5 text-apex-lens ml-0.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="center"
                  sideOffset={6}
                  className="w-80 p-0 bg-apex-shell border border-slate-700 shadow-2xl rounded-xl overflow-hidden"
                  data-testid="evidence-readiness-popover"
                >
                  <div className="px-4 py-3 border-b border-slate-700">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">Evidence Readiness</div>
                    <div className="text-2xl font-bold text-white tabular mt-0.5">{ps.evidence_readiness}%</div>
                    <div className="text-[11px] text-apex-success font-mono mt-0.5 inline-flex items-center gap-0.5">
                      <ArrowUp className="w-2.5 h-2.5" />{ps.evidence_readiness_trend}% from last month
                    </div>
                  </div>

                  {/* 8-week trend sparkline */}
                  <div className="px-4 py-3 border-b border-slate-700" data-testid="readiness-sparkline">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500 font-mono mb-2">8-week trend</div>
                    <div className="flex items-end gap-1 h-8">
                      {[71, 73, 76, 78, 80, 83, 85, 87].map((v, i) => (
                        <motion.div
                          key={i}
                          initial={{ height: 0 }}
                          animate={{ height: `${((v - 68) / 22) * 100}%` }}
                          transition={{ delay: i * 0.05, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                          className="flex-1 rounded-sm"
                          style={{
                            backgroundColor: i === 7 ? '#0D9488' : '#1E293B',
                            border: i === 7 ? '1px solid #0D9488' : '1px solid #334155',
                          }}
                          data-testid={`readiness-sparkline-${i}`}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between text-[9px] font-mono text-slate-600 mt-1">
                      <span>8 weeks ago</span>
                      <span className="text-apex-lens">Today · {ps.evidence_readiness}%</span>
                    </div>
                  </div>
                  <div className="px-4 py-3 space-y-2.5">
                    {READINESS_BREAKDOWN.map((item) => (
                      <div key={item.label} data-testid={`readiness-row-${item.label.split(' ')[0].toLowerCase()}`}>
                        <div className="flex items-center justify-between text-[11px] mb-1">
                          <span className="text-slate-300">{item.label}</span>
                          <span className="font-mono text-white">{item.value}% <span className="text-slate-500">· {item.weight}</span></span>
                        </div>
                        <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${item.value}%` }}
                            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                            className="h-full rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-3 border-t border-slate-700 text-[10px] text-slate-500 font-mono leading-relaxed">
                    Based on 14 months of CloudTrail history · 98.7% event coverage · Last computed: 4 min ago
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="md:hidden">
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="header-readiness-mobile" className="flex items-center gap-1.5 px-2 py-1 rounded bg-slate-800/60">
                    <span className="text-[10px] text-slate-400">ER</span>
                    <span className="text-xs font-bold tabular">{ps.evidence_readiness}%</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="max-w-[280px] text-xs bg-apex-shell text-white border-slate-700">
                  {methodology}
                </PopoverContent>
              </Popover>
            </div>
          </>
        )}

        {/* Search */}
        <GlobalSearch />

        <div className="flex-1 lg:flex-none" />

        {/* Demo button */}
        <button
          onClick={() => setDemoOpen(true)}
          data-testid="header-demo-button"
          className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-apex-lens text-apex-lens hover:bg-apex-lens hover:text-white transition-colors text-[12px] font-semibold"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Demo Mode
        </button>

        <RoleSwitch />

        {/* Status pill */}
        {ps && (
          <div className="hidden xl:flex items-center gap-2 text-[11px] text-slate-400 font-mono">
            <span>v{ps.version}</span>
            <span>·</span>
            <span>{ps.tier}</span>
            <span>·</span>
            <span>{ps.accounts_monitored} accounts</span>
          </div>
        )}
      </div>
    </header>
  );
}
